import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * Static definition of a customer-facing "Services" navbar menu item.
 * These map 1:1 to the links rendered in the navbar Services dropdown.
 */
export interface NavServiceItem {
  /** Stable key used for persistence — must never change once shipped. */
  key: string;
  /** English label (matches navbar link text). */
  labelEng: string;
  /** Thai label (matches navbar link text). */
  labelThai: string;
  /** Route path used by the navbar link. */
  route: string;
}

const API_SERVICE_MENU_URL = environment.API_BASE_URL + '/api/ServiceMenu';

/**
 * Lets admins hide a service from the public "Services" navbar dropdown and
 * footer (e.g. while a service is suspended), without touching the underlying
 * service pages, routes, or service APIs.
 *
 * The hidden state is stored on the server (GET/PUT api/ServiceMenu) so every
 * visitor sees the same menu. It is cached in memory so templates can call
 * isHidden() synchronously; until the first response arrives every service is
 * treated as visible.
 */
@Injectable({
  providedIn: 'root',
})
export class NavServiceVisibilityService {

  private hiddenKeys: string[] = [];

  constructor(private http: HttpClient) {
    this.refresh().subscribe();
  }

  /** Master list of services shown in the navbar "Services" dropdown. */
  private readonly services: NavServiceItem[] = [
    { key: 'laboratory', labelEng: 'Laboratory', labelThai: 'บริการด้านงานทดสอบ', route: 'pages/laboratory' },
    { key: 'rapid-prototype', labelEng: 'Rapid Prototype', labelThai: 'บริการผลิตชิ้นงานต้นแบบด้วยเครื่อง RP', route: 'pages/rapid-prototype' },
    { key: 'design-simulation', labelEng: 'Design & Simulation', labelThai: 'บริการออกแบบ และจําลองแบบทางวิศวกรรม', route: 'pages/design-simulation' },
    { key: 'patents-trademarks', labelEng: 'Patent & Trademark', labelThai: 'บริการด้านงานสิทธิบัตร และเครื่องหมายการค้า', route: 'pages/patents-trademarks' },
    { key: 'product-registration', labelEng: 'Product Registration Support', labelThai: 'บริการด้านงานขึ้นทะเบียน/ขอทุนกับโครงการต่างๆ', route: 'pages/product-registration' },
    { key: 'others', labelEng: 'Other Services', labelThai: 'บริการอื่นๆ', route: 'pages/others' },
    { key: 'project-management', labelEng: 'Project Management', labelThai: 'งานโครงการ', route: 'pages/project-management' },
  ];

  /** Reload the hidden keys from the server (cold observable: subscribe to run). */
  refresh(): Observable<string[]> {
    return this.http.get<{ hiddenKeys: string[] }>(API_SERVICE_MENU_URL).pipe(
      map((res) => (Array.isArray(res?.hiddenKeys) ? res.hiddenKeys : [])),
      tap((keys) => (this.hiddenKeys = keys)),
      // keep last known state on failure; the menu stays fully visible by default
      catchError(() => of(this.hiddenKeys))
    );
  }

  /**
   * All services, annotated with their current hidden state.
   */
  getServices(): (NavServiceItem & { isHidden: boolean })[] {
    return this.services.map((s) => ({ ...s, isHidden: this.hiddenKeys.includes(s.key) }));
  }

  /**
   * Only the services that should currently be shown in the public navbar.
   */
  getVisibleServices(): NavServiceItem[] {
    return this.services.filter((s) => !this.hiddenKeys.includes(s.key));
  }

  isHidden(key: string): boolean {
    return this.hiddenKeys.includes(key);
  }

  /**
   * Persist a service's visibility (admin only). The local cache is updated
   * only after the server accepts the change.
   */
  setHidden(key: string, isHidden: boolean): Observable<unknown> {
    const userToken = localStorage.getItem(environment.authTokenKey);
    const headers = new HttpHeaders({ Authorization: `Bearer ${userToken}` });

    return this.http
      .put(`${API_SERVICE_MENU_URL}/${encodeURIComponent(key)}`, { isHidden }, { headers })
      .pipe(
        tap(() => {
          const idx = this.hiddenKeys.indexOf(key);
          if (isHidden && idx === -1) {
            this.hiddenKeys = [...this.hiddenKeys, key];
          } else if (!isHidden && idx > -1) {
            this.hiddenKeys = this.hiddenKeys.filter((k) => k !== key);
          }
        })
      );
  }
}

